import React, { useEffect, useState } from "react";
import { getLegal, adminUpdateLegal } from "@/lib/api";

const TABS = [
    { slug: "privacy", label: "Privacy Policy" },
    { slug: "terms", label: "Terms of Service" },
];

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

export default function LegalAdminPage() {
    const [tab, setTab] = useState(TABS[0].slug);
    const [data, setData] = useState({ sections: [], updated_at: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [ok, setOk] = useState(false);

    useEffect(() => {
        setLoading(true);
        setOk(false);
        getLegal(tab)
            .then((d) =>
                setData({
                    sections: d.sections || [],
                    updated_at: d.updated_at || "",
                }),
            )
            .finally(() => setLoading(false));
    }, [tab]);

    const setSectionField = (idx, field, value) => {
        setData((d) => {
            const next = [...d.sections];
            next[idx] = { ...next[idx], [field]: value };
            return { ...d, sections: next };
        });
    };
    const setBody = (idx, bidx, value) => {
        setData((d) => {
            const next = [...d.sections];
            const body = [...(next[idx].body || [])];
            body[bidx] = value;
            next[idx] = { ...next[idx], body };
            return { ...d, sections: next };
        });
    };
    const addBody = (idx) =>
        setData((d) => {
            const next = [...d.sections];
            next[idx] = { ...next[idx], body: [...(next[idx].body || []), ""] };
            return { ...d, sections: next };
        });
    const removeBody = (idx, bidx) =>
        setData((d) => {
            const next = [...d.sections];
            next[idx] = {
                ...next[idx],
                body: (next[idx].body || []).filter((_, i) => i !== bidx),
            };
            return { ...d, sections: next };
        });

    const addSection = () =>
        setData((d) => ({
            ...d,
            sections: [...d.sections, { title: "New section", body: [""] }],
        }));
    const removeSection = (idx) =>
        setData((d) => ({
            ...d,
            sections: d.sections.filter((_, i) => i !== idx),
        }));
    const moveSection = (idx, dir) =>
        setData((d) => {
            const next = [...d.sections];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return d;
            [next[idx], next[j]] = [next[j], next[idx]];
            return { ...d, sections: next };
        });

    const save = async () => {
        setSaving(true);
        setOk(false);
        try {
            const cleanedSections = data.sections
                .map((s) => ({
                    title: (s.title || "").trim(),
                    body: (s.body || []).map((p) => p.trim()).filter(Boolean),
                }))
                .filter((s) => s.title || s.body.length);
            const res = await adminUpdateLegal(tab, {
                sections: cleanedSections,
            });
            setData({
                sections: res.sections,
                updated_at: res.updated_at,
            });
            setOk(true);
            setTimeout(() => setOk(false), 2500);
        } catch (e) {
            alert("Save failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="admin-legal">
            <header className="flex items-end justify-between border-b border-[#E6E2D6] pb-6 mb-8">
                <div>
                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                        § Legal — 04
                    </p>
                    <h1 className="font-serif-editorial text-4xl md:text-5xl leading-[1.02] tracking-[-0.02em]">
                        Legal Pages
                    </h1>
                </div>
                <div className="flex items-center gap-6">
                    {ok && (
                        <span
                            data-testid="legal-saved"
                            className="eyebrow text-[#A8844F]"
                        >
                            Saved · date auto-updated
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="legal-save"
                        className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500 disabled:opacity-60"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </header>

            {/* Document switcher */}
            <div className="flex gap-2 mb-6 border-b border-[#E6E2D6]">
                {TABS.map((t) => (
                    <button
                        key={t.slug}
                        type="button"
                        onClick={() => setTab(t.slug)}
                        data-testid={`legal-tab-${t.slug}`}
                        className={`px-4 py-3 font-sans-tight text-[12px] tracking-[0.18em] uppercase transition-colors duration-300 border-b-2 -mb-px ${
                            tab === t.slug
                                ? "border-[#1B1B1B] text-[#1B1B1B]"
                                : "border-transparent text-[#1B1B1B]/50 hover:text-[#1B1B1B]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="flex items-center justify-between mb-8 pb-3 border-b border-[#E6E2D6]">
                <p className="font-sans-tight text-sm text-[#1B1B1B]/70 max-w-2xl font-light">
                    Published at{" "}
                    <span className="text-[#A8844F]">depmont.com/{tab}</span>.
                    Sections are numbered automatically in the order below.
                </p>
                <div className="eyebrow text-[#1B1B1B]/60 shrink-0 pl-6">
                    Last updated · {fmtDate(data.updated_at)}
                </div>
            </div>

            {loading ? (
                <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
            ) : (
                <div className="space-y-8 max-w-4xl">
                    {data.sections.map((s, i) => (
                        <article
                            key={i}
                            data-testid={`legal-section-${i}`}
                            className="border border-[#E6E2D6] p-6 md:p-8"
                        >
                            <div className="flex items-start justify-between mb-4 gap-4">
                                <div className="flex items-baseline gap-3 flex-1">
                                    <span className="font-serif-editorial italic text-[#A8844F] text-2xl">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <input
                                        className="editorial-input"
                                        value={s.title || ""}
                                        onChange={(e) =>
                                            setSectionField(i, "title", e.target.value)
                                        }
                                        placeholder="Section title"
                                        data-testid={`legal-title-${i}`}
                                    />
                                </div>
                                <div className="flex gap-3 shrink-0 pt-3">
                                    <button
                                        type="button"
                                        onClick={() => moveSection(i, -1)}
                                        title="Move up"
                                        className="eyebrow link-underline text-[#1B1B1B]/60"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveSection(i, 1)}
                                        title="Move down"
                                        className="eyebrow link-underline text-[#1B1B1B]/60"
                                    >
                                        ↓
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeSection(i)}
                                        className="eyebrow link-underline text-red-800"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-3 pl-8">
                                {(s.body || []).map((p, bi) => (
                                    <div key={bi}>
                                        <textarea
                                            rows={3}
                                            className="editorial-input resize-none"
                                            value={p}
                                            onChange={(e) => setBody(i, bi, e.target.value)}
                                            placeholder="Paragraph…"
                                            data-testid={`legal-body-${i}-${bi}`}
                                        />
                                        <div className="text-right mt-1">
                                            <button
                                                type="button"
                                                onClick={() => removeBody(i, bi)}
                                                className="eyebrow link-underline text-red-800 text-[10px]"
                                            >
                                                Remove paragraph
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addBody(i)}
                                    className="eyebrow link-underline"
                                >
                                    + Add paragraph
                                </button>
                            </div>
                        </article>
                    ))}

                    <button
                        type="button"
                        onClick={addSection}
                        data-testid="legal-add-section"
                        className="eyebrow link-underline"
                    >
                        + Add section
                    </button>
                </div>
            )}
        </section>
    );
}
